import { getAttribution } from "@/lib/attribution";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// The new RPCs aren't in the generated Database types yet; cast through a
// narrow wrapper so the rest of the code stays typed.
export const rpc = (fn: string, args?: Record<string, unknown>) =>
  (supabase.rpc as unknown as (fn: string, args?: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>)(fn, args);

export interface ExplorerRow {
  row_type: 'role' | 'combo' | 'metro';
  role_key: string | null;
  metro_key: string | null;
  jobs_total: number;
  mom_pct: number | null;
  median_salary: number | null;
  salary_currency: string | null;
  salary_n: number | null;
  remote_pct: number | null;
  suppressed: boolean;
  monthly_series: number[] | null;
  salary_series: (number | null)[] | null;
}

export interface PulseMonth {
  month: string;
  total_jobs: number;
  remote_jobs: number;
  pct_remote: number;
  pct_disclosed: number;
}

export interface DisclosureCompany {
  company: string;
  postings: number;
  disclosure_pct: number;
  /** hand/agent-verified: employer shows pay on its own career site (employer_site_audit) */
  site_discloses: boolean | null;
}

export interface IndustryRow {
  industry: string;
  jobs_total: number;
  mom_pct: number | null;
  median_salary: number | null;
  salary_n: number | null;
  remote_pct: number | null;
  disclosed_pct: number | null;
  ai_pct: number | null;
  suppressed: boolean;
  monthly_series: number[] | null;
}

export interface RoleIndustryRow {
  industry: string;
  jobs_total: number;
  median_salary: number | null;
  salary_n: number | null;
  disclosed_pct: number | null;
  remote_pct: number | null;
  suppressed: boolean;
}

/** One role, market median beside the curated top-of-market median. Never blended. */
export interface TopOfMarketRow {
  canonical_role: string;
  market_median: number;
  market_n: number;
  top_median: number;
  top_n: number;
  top_employers: number;
  top_ceiling: number;
  premium_ratio: number;
  premium_abs: number;
  currency: string;
}

export interface TopCompRow {
  company: string;
  industry: string;
  title: string;
  location: string | null;
  locations: number;
  is_remote: boolean;
  comp_min: number | null;
  comp_max: number | null;
  comp_currency: string;
  offers_equity: boolean;
  posting_url: string | null;
}

/** A single live posting paying well above the market rate for its own title. */
export interface OutlierJob {
  company: string;
  title: string;
  canonical_role: string;
  industry: string;
  location: string | null;
  locations: number;
  is_remote: boolean;
  comp_min: number;
  comp_max: number;
  comp_mid: number;
  market_median: number;
  market_n: number;
  premium_multiple: number;
  premium_abs: number;
  offers_equity: boolean;
  posting_url: string | null;
  currency: string;
}

export interface RolePercentiles {
  p10: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  n: number;
}

export async function unwrap<T>(p: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(error.message);
  return data as T;
}

const STALE = 1000 * 60 * 30; // data changes monthly; cache aggressively

export function useExplorerRows(tab: 'jobs' | 'internships') {
  return useQuery({
    queryKey: ['explorer_rows', tab],
    queryFn: () => unwrap<ExplorerRow[]>(rpc('explorer_rows', { p_tab: tab })),
    staleTime: STALE,
  });
}

export function useMarketPulse() {
  return useQuery({
    queryKey: ['market_pulse'],
    queryFn: () => unwrap<PulseMonth[]>(rpc('market_pulse')),
    staleTime: STALE,
  });
}

export function useDisclosureCompanies(direction: 'least' | 'most', enabled = true) {
  return useQuery({
    queryKey: ['disclosure_companies', direction],
    queryFn: () => unwrap<DisclosureCompany[]>(rpc('disclosure_companies', { p_direction: direction })),
    staleTime: STALE,
    enabled,
  });
}

export function useIndustryRows(tab: 'jobs' | 'internships', enabled = true) {
  return useQuery({
    queryKey: ['industry_rows', tab],
    queryFn: () => unwrap<IndustryRow[]>(rpc('industry_rows', { p_tab: tab })),
    staleTime: STALE,
    enabled,
  });
}

export function useRoleIndustryRows(roleKey: string | null) {
  return useQuery({
    queryKey: ['role_industry_rows', roleKey],
    queryFn: () => unwrap<RoleIndustryRow[]>(rpc('role_industry_rows', { p_role: roleKey })),
    staleTime: STALE,
    enabled: !!roleKey,
  });
}

export function useRolePercentiles(roleKey: string | null) {
  return useQuery({
    queryKey: ['role_percentiles', roleKey],
    queryFn: async () => {
      const rows = await unwrap<RolePercentiles[]>(rpc('role_percentiles', { p_role: roleKey }));
      return rows?.[0] ?? null;
    },
    staleTime: STALE,
    enabled: !!roleKey,
  });
}

export function useTopOfMarket(enabled = true) {
  return useQuery({
    queryKey: ['top_of_market_premium'],
    queryFn: () => unwrap<TopOfMarketRow[]>(rpc('top_of_market_premium')),
    staleTime: STALE,
    enabled,
  });
}

export function useTopComp(limit = 25, enabled = true) {
  return useQuery({
    queryKey: ['priority_top_comp', limit],
    queryFn: () => unwrap<TopCompRow[]>(rpc('priority_top_comp', { p_limit: limit })),
    staleTime: STALE,
    enabled,
  });
}

export function useOutlierJobs(minMultiple = 1.25, limit = 60, enabled = true) {
  return useQuery({
    queryKey: ['outlier_premium_jobs', minMultiple, limit],
    queryFn: () => unwrap<OutlierJob[]>(rpc('outlier_premium_jobs', { p_limit: limit, p_min_multiple: minMultiple })),
    staleTime: STALE,
    enabled,
  });
}

/** Above-market postings scoped to one role, for the role detail page. */
export function useOutlierJobsForRole(roleKey: string | null, minMultiple = 1.25) {
  return useQuery({
    queryKey: ['outlier_premium_jobs_role', roleKey, minMultiple],
    queryFn: () => unwrap<OutlierJob[]>(rpc('outlier_premium_jobs', {
      p_limit: 25, p_min_multiple: minMultiple, p_role: roleKey,
    })),
    staleTime: STALE,
    enabled: !!roleKey,
  });
}

/** Newly-posted above-market jobs — powers the live feed and its embed. */
export function useNewAboveMarketJobs(days = 14, minMultiple = 1.25, limit = 25) {
  return useQuery({
    queryKey: ['new_above_market_jobs', days, minMultiple, limit],
    queryFn: () => unwrap<(OutlierJob & { first_seen: string; days_old: number })[]>(
      rpc('new_above_market_jobs', { p_days: days, p_min_multiple: minMultiple, p_limit: limit })),
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
  });
}

/** The public above-market feed. p_delay_hours powers the free/delayed embed. */
export function useAboveMarketFeed(
  limit = 100, minMultiple = 1.25, minSalary: number | null = null,
  delayHours = 0, group: string | null = null, search: string | null = null,
) {
  return useQuery({
    queryKey: ['above_market_feed', limit, minMultiple, minSalary, delayHours, group, search],
    queryFn: () => unwrap<FeedJob[]>(rpc('above_market_feed', {
      p_limit: limit, p_min_multiple: minMultiple, p_min_salary: minSalary,
      p_delay_hours: delayHours, p_group: group, p_search: search,
    })),
    staleTime: 1000 * 60 * 10,
    // Keep the current rows up (the page dims them) while a filter change loads.
    placeholderData: keepPreviousData,
  });
}

/** Per-category counts for the above-market chips, computed with the same
 *  predicates as the feed so the chips sum to the total printed beside them. */
export interface FeedFacet {
  function_group: string;
  n: number;
  n_mult_150: number;
  n_mult_200: number;
  n_pay_175k: number;
  n_pay_250k: number;
}

export function useAboveMarketFacets() {
  return useQuery({
    queryKey: ['above_market_facets'],
    queryFn: () => unwrap<FeedFacet[]>(rpc('above_market_facets', {})),
    staleTime: 1000 * 60 * 30,
  });
}

export interface FeedJob extends OutlierJob {
  function_group: string;
  first_seen: string;
  posted_at: string | null;
  days_old: number;
  posted_days_ago: number | null;
  /** When we last re-read this posting on the employer's own board. */
  last_seen: string | null;
}

export interface CpaFirmRow {
  firm_name: string;
  ownership: 'pe_backed' | 'independent' | 'esop' | 'public' | 'unknown';
  backer: string | null;
  /** set when the firm operates under its own name but rolls up to a platform */
  parent_platform: string | null;
  deal_announced: string | null;
  source_url: string | null;
  hq_location: string | null;
  careers_url: string | null;
  /** the firm's own site — who a user would actually be working with */
  website_url: string | null;
  live_postings: number;
  with_pay: number;
  remote_postings: number;
  median_advertised: number | null;
}

/** Verified-only: the RPC filters to status='verified', and the DB refuses a
 *  verified row without a source_url. Unsourced claims cannot reach this hook. */
export function useCpaFirmTracker() {
  return useQuery({
    queryKey: ['cpa_firm_tracker'],
    queryFn: () => unwrap<CpaFirmRow[]>(rpc('cpa_firm_tracker')),
    staleTime: 1000 * 60 * 30,
  });
}

export interface AboveMarketMeta {
  last_swept_at: string | null;
  next_sweep_at: string | null;
  sweep_every_hours: number;
  open_postings: number;
  above_market_jobs: number;
  employers: number;
  new_last_24h: number;
}

/** Freshness stamps for the above-market feed. Short staleTime so a visitor who
 *  leaves the tab open sees the clock roll over after a sweep. */
export function useAboveMarketMeta() {
  return useQuery({
    queryKey: ['above_market_meta'],
    queryFn: async () => {
      const rows = await unwrap<AboveMarketMeta[]>(rpc('above_market_meta'));
      return rows?.[0] ?? null;
    },
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });
}

/**
 * Above-market job interest goes to the JOB ALERT flow, not the waitlist.
 *
 * The waitlist asks people to confirm by email and then sends a digest. Measured
 * over its whole life it converted 0 of 6 real people, while the job alert form
 * has 1,363 because it gives something back immediately and carries its own
 * unsubscribe token. So a visitor who wants a specific above-market role gets a
 * real alert for that role rather than a confirmation email they will not open.
 */
export async function submitAboveMarketAlert(payload: {
  email: string;
  jobTitle?: string | null;
  companyName?: string | null;
  location?: string | null;
  isRemote?: boolean;
  /** job_alerts.source: data_gate (JobGate modal) or go_interstitial (/go). */
  source?: 'data_gate' | 'go_interstitial';
}) {
  // create-job-alert requires at least one of jobTitle / companyName.
  if (!payload.jobTitle && !payload.companyName) {
    throw new Error('A job title or company is required to create an alert');
  }
  const { error } = await supabase.functions.invoke('create-job-alert', {
    body: {
      email: payload.email,
      jobTitle: payload.jobTitle ?? undefined,
      companyName: payload.companyName ?? undefined,
      location: payload.location ?? undefined,
      isRemote: payload.isRemote ?? false,
      isInternship: false,
      isContract: false,
      isPartTime: false,
      frequency: 'daily',
      source: payload.source ?? 'data_gate',
    },
  });
  if (error) throw new Error(error.message);
}

export async function submitWaitlistInterest(payload: {
  email: string;
  source: string;
  intent: 'alert' | 'csv' | 'counsellor' | 'campus_deadlines';
  role_key?: string | null;
  metro_key?: string | null;
  metric?: string | null;
  context?: Record<string, unknown>;
}) {
  const attr = getAttribution();
  const { error } = await (supabase.from as unknown as (t: string) => {
    insert: (v: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
  })('waitlist_interest').insert({
    email: payload.email,
    source: payload.source,
    intent: payload.intent,
    role_key: payload.role_key ?? null,
    metro_key: payload.metro_key ?? null,
    metric: payload.metric ?? null,
    // First/last-touch attribution (src/lib/attribution.ts) rides in context; newsletter-signup-sync ignores
    // keys it does not know.
    context: { ...(payload.context ?? {}), ...(attr ? { attribution: attr } : {}) },
  });
  if (error) throw new Error(error.message);
}

export interface CpaPremiumRow {
  canonical_role: string;
  function_group: string;
  n_cpa_required: number;
  n_no_mention: number;
  median_cpa_required: number;
  median_no_mention: number;
  premium_abs: number;
  premium_multiple: number;
  currency: string;
}

/** What the CPA licence is worth, compared WITHIN a canonical role. Comparing
 *  across roles would just measure seniority — CPA-required postings skew senior,
 *  so an unmatched comparison reads ~$60k when the real within-role gap is far
 *  smaller for junior titles and larger for controller-level ones.
 *  Reads a nightly-refreshed table: computing the percentiles live timed out for
 *  the anon role over 66k postings. */
export function useCpaPremium(currency = 'USD') {
  return useQuery({
    queryKey: ['cpa_premium', currency],
    queryFn: () => unwrap<CpaPremiumRow[]>(rpc('cpa_premium', { p_min_each: 25, p_currency: currency })),
    staleTime: 1000 * 60 * 60,
  });
}

export interface CpaPremiumMeta {
  computed_at: string;
  roles: number;
  postings: number;
  /** Negative: the more often the licence is demanded, the smaller the premium. */
  corr_cpa_share: number;
}

/** Freshness + the headline correlation, so the page can prove it is live and
 *  quote a number it computed rather than one someone typed. */
export function useCpaPremiumMeta(currency = 'USD') {
  return useQuery({
    queryKey: ['cpa_premium_meta', currency],
    queryFn: async () => {
      const rows = await unwrap<CpaPremiumMeta[]>(rpc('cpa_premium_meta', { p_currency: currency }));
      return rows?.[0] ?? null;
    },
    staleTime: 1000 * 60 * 60,
  });
}

export interface RemoteFirstRow {
  company: string;
  industry: string | null;
  distinct_roles: number;
  remote_roles: number;
  remote_pct: number;
  total_postings: number;
  with_pay: number;
  median_advertised: number | null;
  example_apply_url: string | null;
  /** 'confirmed' = employer's own swept board agrees; 'contradicted' rows are
   *  excluded server-side; 'unswept' = we don't read their board directly. */
  board_check: 'confirmed' | 'contradicted' | 'unswept' | null;
}

/** Employers whose accounting and finance hiring is genuinely remote. Ranked by
 *  DISTINCT remote roles, not posting count — counting postings rewarded whoever
 *  duplicated one job across the most cities. */
export function useRemoteFirstEmployers(minRemotePct = 70, limit = 50) {
  return useQuery({
    queryKey: ['remote_first_employers', minRemotePct, limit],
    queryFn: () => unwrap<RemoteFirstRow[]>(rpc('remote_first_employers', {
      p_limit: limit, p_min_roles: 3, p_min_remote_pct: minRemotePct,
    })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface CpaFirmPayRow {
  canonical_role: string;
  function_group: string;
  n_firm: number;
  n_industry: number;
  firm_median: number;
  industry_median: number;
  delta_abs: number;
  delta_multiple: number;
}

/** What a CPA firm pays versus industry for the SAME role. Big 4 are excluded
 *  from the firm side — the question is what a normal firm pays, and three
 *  employers would otherwise carry the comparison. */
export interface CpaPremiumExample {
  canonical_role: string;
  premium_multiple: number;
  cpa_company: string; cpa_title: string; cpa_pay: number; cpa_url: string;
  non_company: string; non_title: string; non_pay: number; non_url: string;
}

/** Live postings that demonstrate each premium — one demanding the licence, one
 *  not, each the posting closest to its own side's median so the pair reproduces
 *  the published figure rather than flattering it. */
export function useCpaPremiumExamples(currency = 'USD', maxRoles = 8) {
  return useQuery({
    queryKey: ['cpa_premium_examples', currency, maxRoles],
    queryFn: () => unwrap<CpaPremiumExample[]>(
      rpc('cpa_premium_examples', { p_currency: currency, p_max_roles: maxRoles })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface CpaFirmPayMeta {
  computed_at: string;
  roles: number;
  firm_above: number;
  firm_below: number;
  postings: number;
}

/** Freshness plus the above/below split, so the page states the split it
 *  measured rather than one written into the copy by hand. */
export function useCpaFirmPayMeta() {
  return useQuery({
    queryKey: ['cpa_firm_pay_meta'],
    queryFn: async () => {
      const rows = await unwrap<CpaFirmPayMeta[]>(rpc('cpa_firm_pay_meta', {}));
      return rows?.[0] ?? null;
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useCpaFirmPay() {
  return useQuery({
    queryKey: ['cpa_firm_pay'],
    queryFn: () => unwrap<CpaFirmPayRow[]>(rpc('cpa_firm_pay')),
    staleTime: 1000 * 60 * 60,
  });
}

export interface CpaFirmHiringRow {
  company: string;
  tier: 'big4' | 'national' | 'regional';
  live_postings: number;
  distinct_roles: number;
  remote_postings: number;
  with_pay: number;
  median_advertised: number | null;
  example_apply_url: string | null;
}

/** CPA firms hiring now, ranked by DISTINCT roles. One firm had 280 listings
 *  that were only 9 real roles duplicated across locations. */
export function useCpaFirmHiring(tier: 'regional' | 'national' | 'all' = 'regional', limit = 40) {
  return useQuery({
    queryKey: ['cpa_firm_hiring', tier, limit],
    queryFn: () => unwrap<CpaFirmHiringRow[]>(rpc('cpa_firm_hiring_list', { p_limit: limit, p_tier: tier })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface LayoffMeta {
  last_refreshed: string | null;
  next_refresh: string | null;
  refresh_every_hours: number;
  verified_events: number;
  reported_pool: number;
}

/** The tracker's heartbeat: last time the 3-hour refresh actually ran. */
export function useLayoffMeta() {
  return useQuery({
    queryKey: ['layoff_meta'],
    queryFn: async () => {
      const rows = await unwrap<LayoffMeta[]>(rpc('layoff_meta'));
      return rows?.[0] ?? null;
    },
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 5,
  });
}

/* ── Ontario public-sector pay (the sunshine list) ─────────────────────────
 * Every Ontario public-sector salary of $100k+ is disclosed each March. We
 * keep accounting and finance titles only, and never store names: the layer
 * that matters is employer x title x salary. */
export interface SunshineOntarioPage {
  year: number;
  province: string;
  counts: { rows: number; employers: number; median: number; p25: number; p75: number; over_150k: number; over_200k: number };
  by_bucket: { bucket: string; n: number; p25: number; median: number; p75: number; max: number }[];
  by_city: { city: string; n: number; median: number }[];
  /** One row per year x source; lets a province with several years show a trend. */
  by_year: { year: number; sector: string; basis: 'base_salary' | 'compensation'; n: number; median: number }[];
  top_employers: { employer: string; sector: string; city: string | null; basis: string; n: number; median: number; max: number }[];
  top_paid: { employer: string; title: string; salary: number; city: string | null; basis: string }[];
  top_titles: { title: string; n: number; median: number }[];
  computed_at: string;
}

/** Same table and RPC serve every province; the page picks copy per province. */
export function useSunshineOntario(year = 2025, province = 'ON') {
  return useQuery({
    queryKey: ['sunshine_ontario_page', year, province],
    queryFn: () => unwrap<SunshineOntarioPage>(rpc('sunshine_ontario_page', { p_year: year, p_province: province })),
    staleTime: 1000 * 60 * 60,
  });
}

/** Cross-province public pay: one comparable (latest complete) list per province and basis. */
export interface PublicPayCompareSeries {
  province: string;              // ON, AB, BC, NL, MB
  basis: 'base_salary' | 'compensation';
  year: number;
  counts: { rows: number; employers: number; median: number; p25: number; p75: number; floor: number; top: number };
  by_bucket: { bucket: string; n: number; p25: number; median: number; p75: number; max: number }[];
  top_paid: { title: string; employer: string; salary: number }[];
  by_year: { year: number; n: number; median: number }[];
}
export interface PublicPayCompare { series: PublicPayCompareSeries[]; computed_at: string }
/** Live Canadian postings grouped into the public-pay levels (hourly cache). */
export interface PublicPayMarket {
  levels: { bucket: string; live: number; priced: number; p25: number | null; median: number | null; p75: number | null; roles: string[] }[];
  live_total: number; priced_total: number; computed_at: string;
}
export function usePublicPayMarket() {
  return useQuery({
    queryKey: ['public_pay_market'],
    queryFn: () => unwrap<PublicPayMarket>(rpc('public_pay_market')),
    staleTime: 1000 * 60 * 30,
  });
}
export function usePublicPayCompare() {
  return useQuery({
    queryKey: ['public_pay_compare'],
    queryFn: () => unwrap<PublicPayCompare>(rpc('public_pay_compare')),
    staleTime: 1000 * 60 * 60,
  });
}

/** One calendar month of the 12-month series, over EVERY verified event. */
export interface LayoffRecentMonth {
  m: string;                  // 2026-01
  events: number;
  cut: number;                // roles announced cut, summed
  open: number;               // finance roles those employers have open today, once per employer
  biggest: number;            // the largest single announcement that month
  biggest_co: string | null;
}
export interface LayoffRecent {
  months: LayoffRecentMonth[];
  events: number; cut: number; employers: number; open: number;   // 12-month totals, employers distinct
}
export interface LayoffTrendsPayload {
  monthly: { m: string; events: number; heads: number }[];
  ai_quarters: { q: string; ai_yes: number; ai_no: number; ai_heads: number }[];
  industries_ytd: { industry: string; events: number; heads: number }[];
  finance: { companies_cutting: number; still_hiring: number; open_roles: number; with_pay: number };
  /** Added 2026-09-05. Optional only so a cache row from before the refresh
   *  function learned it still parses; the chart renders nothing until it lands. */
  recent?: LayoffRecent;
  computed_at: string;
}

/** Archive-wide trend series (78 months, AI attribution, industry mix, the
 *  cutting-vs-hiring join). Served from layoff_trends_cache, rebuilt hourly:
 *  the live computation ran 2.5s+ and coin-flipped the anon 3s timeout, so the
 *  RPC reads one cached jsonb row instead. */
export function useLayoffTrends() {
  return useQuery({
    queryKey: ['layoff_trends'],
    queryFn: () => unwrap<LayoffTrendsPayload>(rpc('layoff_trends')),
    staleTime: 1000 * 60 * 30,
  });
}

export interface LayoffRow {
  company: string;
  headcount: number | null;
  pct: number | null;
  scope: string;
  announced_on: string;
  effective_on: string | null;
  industry: string | null;
  hq: string | null;
  country: string | null;
  stage: string | null;
  source_url: string;
  source_name: string;
  /** Secondary proof — a state WARN notice. Never the displayed link: it is a
   *  900-row spreadsheet download, not something a reader can act on. */
  corroboration_url: string | null;
  corroboration_name: string | null;
  /** Headcount as legally filed, which counts ONE state. `headcount` carries the
   *  press figure for the whole action, so these diverge: Visa filed 320 in
   *  California as part of ~2,600 company-wide. */
  warn_headcount: number | null;
  note: string | null;
  live_postings: number;
  with_pay: number;
  median_advertised: number | null;
  example_apply_url: string | null;
}

/** Verified + company-confirmed layoffs only. The RPC filters to them and the
 *  DB refuses to mark an event verified without a source and a confirmation, so
 *  "considering" stories cannot reach this hook. */
export function useLayoffTracker(limit = 40, sort: 'recent' | 'headcount' = 'recent') {
  return useQuery({
    queryKey: ['layoff_tracker', limit, sort],
    queryFn: () => unwrap<LayoffRow[]>(rpc('layoff_tracker', { p_limit: limit, p_sort: sort })),
    staleTime: 1000 * 60 * 30,
  });
}

/** AI companies hiring accountants — curated ai-native employers, every number
 *  read directly from the company's own ATS board by the 4h sweep. */
export interface AiCompanyRow {
  company: string;
  /** Accounting & finance roles only — the slice we track in depth. */
  open_roles: number;
  with_pay: number;
  median_advertised: number | null;
  above_market: number;
  remote_roles: number;
  /** EVERY open role on their board, all disciplines, from board-scan. NULL for
   *  Workday employers: its API returns search subsets, so a board-wide
   *  denominator is unknowable there and a fabricated one is worse than none. */
  all_roles: number | null;
  all_remote: number | null;
  board_checked_at: string | null;
  example_apply_url: string | null;
  newest_posted: string | null;
}

export function useAiCompanies() {
  return useQuery({
    queryKey: ['ai_companies_hiring'],
    queryFn: () => unwrap<AiCompanyRow[]>(rpc('ai_companies_hiring')),
    staleTime: 1000 * 60 * 30,
  });
}

/** Posting hygiene — how long each employer's "open" roles have really been
 *  open, from ATS-verified publish dates. Stale-first ordering. */
export interface PostingHygieneRow {
  company: string;
  open_roles: number;
  aged_roles: number;
  stale_180d: number;
  pct_stale: number | null;
  median_age_days: number | null;
  oldest_posted: string | null;
  newest_posted: string | null;
  /** Newest posting on that board — the page names employers for stale boards
   *  and gave the reader nowhere to go to check. */
  example_apply_url: string | null;
}

/** Verified remote-hiring companies — board-wide (all disciplines), measured
 *  by board-scan from each employer's own careers page. */
export interface RemoteCompanyRow {
  company: string;
  industry: string | null;
  board_total: number;
  board_remote: number;
  remote_pct: number;
  af_roles: number;
  af_remote: number;
  af_median: number | null;
  af_with_pay: number;
  example_apply_url: string | null;
  listed_since: string | null;
}

export function useRemoteCompanies(minPct = 100, limit = 300) {
  return useQuery({
    queryKey: ['remote_companies', minPct, limit],
    queryFn: () => unwrap<RemoteCompanyRow[]>(rpc('remote_companies', { p_limit: limit, p_min_pct: minPct })),
    staleTime: 1000 * 60 * 30,
  });
}

/** Companies with remote roles a Canadian can actually take, counted from
 *  their own boards by the same scanner. */
export interface RemoteCompanyCARow {
  company: string;
  industry: string | null;
  board_total: number;
  board_remote: number;
  remote_pct: number;
  board_ca: number;
  board_ca_remote: number;
  example_apply_url: string | null;
  listed_since: string | null;
}

export function useRemoteCompaniesCA(limit = 100) {
  return useQuery({
    queryKey: ['remote_companies_ca', limit],
    queryFn: () => unwrap<RemoteCompanyCARow[]>(rpc('remote_companies_ca', { p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

/** AI-native companies we watch that have no finance role open right now.
 *  Showing them is the honest version of "we track 60 AI companies". */
export interface AiWatchRow { company: string; industry: string | null; last_checked: string | null }

export function useAiWatchlist() {
  return useQuery({
    queryKey: ['ai_companies_watchlist'],
    queryFn: () => unwrap<AiWatchRow[]>(rpc('ai_companies_watchlist')),
    staleTime: 1000 * 60 * 30,
  });
}

/** "What we screen out", by measured reason. Aggregate only — no employer is
 *  named, and every category is an observable fact about a posting rather than
 *  an inference about intent. */
export interface ScreeningRow {
  category: string; label: string; blurb: string;
  n: number; denominator: number; pct: number | null;
}

export function useCurationScreening() {
  return useQuery({
    queryKey: ['curation_screening'],
    queryFn: () => unwrap<ScreeningRow[]>(rpc('curation_screening')),
    staleTime: 1000 * 60 * 30,
  });
}

/** The wire: one chronological stream of real events across every dataset. */
export interface LiveEvent {
  /** When the event entered the wire — NOT the job's age. For postings that is
   *  when we first saw it on the employer's board; `posted_at` below is the
   *  employer's own publish date, which is the number a candidate cares about. */
  at: string; kind: 'above_market' | 'layoff' | 'remote' | 'ownership' | 'still_open';
  /** The sentence arrives in parts so the page can set the company bold and the
   *  figure in colour without pulling a finished string apart with a regex. */
  company: string; lead: string; figure: string; tail: string;
  detail: string | null;
  /** Only where a separate verification step happened — reading an employer's
   *  own board is the baseline here and earns no mark. */
  verified_label: string | null;
  href: string;
  /** Postings only; null on layoff/remote/ownership events. */
  location: string | null;
  /** The employer's own publish date, where they state one. */
  posted_at: string | null;
  /** Postings only: when we last re-read the employer's own board and found
   *  this posting still there. This is the claim nobody else publishes. */
  last_seen: string | null;
}

export function useLiveFeed(limit = 60) {
  return useQuery({
    queryKey: ['live_feed', limit],
    queryFn: () => unwrap<LiveEvent[]>(rpc('live_feed', { p_limit: limit })),
    staleTime: 1000 * 30,
    refetchInterval: 60_000,   // the page counts down to this; keep them equal
    refetchOnWindowFocus: true,
  });
}

export function usePostingHygiene() {
  return useQuery({
    queryKey: ['posting_hygiene'],
    queryFn: () => unwrap<PostingHygieneRow[]>(rpc('posting_hygiene')),
    staleTime: 1000 * 60 * 30,
  });
}

export interface CurationStats {
  screened_all_time: number;
  live_now: number;
  removed_all_time: number;
  removed_pct: number | null;
  live_with_expiry: number;
  live_past_expiry: number;
  median_hours_to_removal: number | null;
  removed_within_a_day: number | null;
  median_age_days: number | null;
  posted_last_7d: number | null;
  posted_last_30d: number | null;
  flagged_duplicate: number | null;
  computed_at: string;
}

export interface CurationDay {
  d: string;
  pulled: number | null;
  kept: number | null;
  discarded_seen: number | null;
  removed: number | null;
}

/** Board-hygiene figures. Reports outcomes only — never the rules behind them. */
export function useCurationStats() {
  return useQuery({
    queryKey: ['curation_stats'],
    queryFn: async () => {
      const rows = await unwrap<CurationStats[]>(rpc('curation_stats'));
      return rows?.[0] ?? null;
    },
    staleTime: 1000 * 60 * 30,
  });
}

export function useCurationDaily(days = 21) {
  return useQuery({
    queryKey: ['curation_daily', days],
    queryFn: () => unwrap<CurationDay[]>(rpc('curation_daily', { p_days: days })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface CurationIntakeRow {
  d: string;
  stage: 'fetch' | 'cleanup';
  reason: string;
  n: number;
}

/** The intake funnel, per day and drop reason. Counters accrue live within the
 *  day (they only grow), so today is included here unlike the turnover series. */
export function useCurationIntake(days = 14) {
  return useQuery({
    queryKey: ['curation_intake', days],
    queryFn: () => unwrap<CurationIntakeRow[]>(rpc('curation_intake', { p_days: days })),
    staleTime: 1000 * 60 * 10,
  });
}

export interface ReportedLayoff {
  company: string;
  laid_off: number | null;
  pct: number | null;
  event_date: string;
  industry: string | null;
  country: string | null;
  source_url: string;
  live_postings: number;
  example_apply_url: string | null;
}

/** Press-reported layoffs from the discovery layer — explicitly NOT verified
 *  by us. Events we verify graduate to the table above and leave this feed. */
export function useReportedLayoffs(days = 365, limit = 60) {
  return useQuery({
    queryKey: ['layoff_reported', days, limit],
    queryFn: () => unwrap<ReportedLayoff[]>(rpc('layoff_reported', { p_days: days, p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

// ---------- early-career hub ----------

export interface EarlyCareerJob {
  short_id: string;
  title: string;
  company: string;
  location: string | null;
  is_remote: boolean;
  ec_type: 'intern' | 'coop' | 'new_grad' | 'first_job';
  term: string | null;
  sponsorship: 'yes' | 'no' | 'mentioned' | null;
  cpa_support: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_period: string | null;
  currency: string | null;
  /** AF Estimate midpoint, only set when the posting itself discloses nothing */
  est_p50: number | null;
  est_confidence: string | null;
  posted_at: string | null;
  apply_link: string | null;
  /** 'board' = our index; 'direct' = read from the employer's own career site */
  source: 'board' | 'direct';
  sector: 'cpa_firm' | 'industry' | null;
  industry: string | null;
  /** minimum GPA the posting states, parsed conservatively (null = not stated) */
  gpa_min: number | null;
  /** application deadline when the posting states one unambiguously */
  apply_by: string | null;
  /** what the posting text says, read hourly with fixed rules (early_career_signals):
   *  hourly_low/high, hourly_rate, paid, hybrid, remote, mentorship, rotational, housing,
   *  conversion, deadline, start_window, cpa_exam, cpa_canada, class_year, transcript,
   *  cover_letter, citizenship, busy_season, asks_2plus_years. Absent for direct rows. */
  signals?: Record<string, boolean | number> | null;
}

export function useEarlyCareerFeed(
  type: string | null, term: string | null, country: string | null,
  search: string | null, limit = 100, sector: string | null = null,
  remote: boolean | null = null,
) {
  return useQuery({
    queryKey: ['early_career_feed', type, term, country, search, limit, sector, remote],
    queryFn: () => unwrap<EarlyCareerJob[]>(rpc('early_career_feed', {
      p_type: type, p_term: term, p_country: country, p_search: search,
      p_limit: limit, p_sector: sector, p_remote: remote,
    })),
    staleTime: 1000 * 60 * 10,
  });
}

export interface InternTopEmployer {
  period: string; rank: number; company: string; sector: string | null;
  interns: number; coops: number; new_grads: number;
  with_pay: number; median_advertised: number | null; careers_url: string | null;
  computed_at: string;
}

export function useInternTopEmployers(period: string | null = null) {
  return useQuery({
    queryKey: ['intern_top_employers', period],
    queryFn: () => unwrap<InternTopEmployer[]>(rpc('intern_top_employers', { p_period: period })),
    staleTime: 1000 * 60 * 60,
  });
}

export interface EarlyCareerFirm {
  company: string;
  sector: 'cpa_firm' | 'industry' | null;
  interns: number; coops: number; new_grads: number; first_jobs: number;
  /** new-grad or first-job postings whose text says the work is seasonal
   *  (tax-season preparer chains); kept out of new_grads and first_jobs */
  seasonal: number;
  with_pay: number;
  median_advertised: number | null;
  /** a board short_id, or an external posting URL for direct-only employers */
  example_ref: string | null;
  /** an external apply URL, resolvable to the employer's career page root */
  example_apply_url: string | null;
}
export function useEarlyCareerFirms(sector: string | null, limit = 60) {
  return useQuery({
    queryKey: ['early_career_firms', sector, limit],
    queryFn: () => unwrap<EarlyCareerFirm[]>(rpc('early_career_firms', {
      p_sector: sector, p_limit: limit,
    })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface EarlyCareerFacet { dim: string; val: string; n: number }
export function useEarlyCareerFacets() {
  return useQuery({
    queryKey: ['early_career_facets'],
    queryFn: () => unwrap<EarlyCareerFacet[]>(rpc('early_career_facets', {})),
    staleTime: 1000 * 60 * 30,
  });
}

export interface EarlyCareerPayRow {
  canonical_role: string;
  metro_area: string | null;
  country: string;
  n: number;
  p25: number; p50: number; p75: number;
}
export function useEarlyCareerPay(currency: 'USD' | 'CAD') {
  return useQuery({
    queryKey: ['early_career_pay', currency],
    queryFn: () => unwrap<EarlyCareerPayRow[]>(rpc('early_career_pay_table', { p_currency: currency })),
    staleTime: 1000 * 60 * 60,
  });
}

export interface SeasonalityMonth { month: string; intern_posted: number; all_posted: number; share: number }
export function useEarlyCareerSeasonality() {
  return useQuery({
    queryKey: ['early_career_seasonality'],
    queryFn: () => unwrap<SeasonalityMonth[]>(rpc('early_career_seasonality_curve', {})),
    staleTime: 1000 * 60 * 60,
  });
}

export interface CpaSupportEmployer {
  company: string; n_flagged: number; n_active: number;
  has_early_career: boolean; example_short_id: string | null;
  example_apply_url: string | null;
}
export function useCpaSupportEmployers(limit = 50) {
  return useQuery({
    queryKey: ['cpa_support_employers', limit],
    queryFn: () => unwrap<CpaSupportEmployer[]>(rpc('cpa_support_employers', { p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface SponsorshipEmployer {
  company: string; sponsors_n: number; no_sponsor_n: number;
  mentions_n: number; example_short_id: string | null;
}
export function useSponsorshipEmployers(limit = 100) {
  return useQuery({
    queryKey: ['sponsorship_employers', limit],
    queryFn: () => unwrap<SponsorshipEmployer[]>(rpc('sponsorship_employers', { p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface EarlyCareerMeta {
  total: number; interns: number; coops: number; new_grad: number;
  first_jobs: number; with_pay: number; direct_n: number; last_refreshed: string | null;
}
/** CPA pre-approved training offices (PPR), by jurisdiction, joined to live hiring. */
export interface CpaPprPage {
  counts: { offices: number; firms: number; provinces: number; hiring_now: number };
  by_province: { province: string; offices: number; firms: number; source_url: string; source_note: string }[];
  by_city: { city: string; province: string; offices: number }[];
  firms: { firm: string; provinces: string[]; offices: number; cities: string[] | null; types: string[] | null; big4: boolean; live: number; early_career: number; apply_url: string | null }[];
  refreshed_at: string | null;
}
/** Campus employer profiles: one row per employer with 5+ live campus postings
 *  (mv_campus_employers), plus the live feed for one employer. */
export interface CampusEmployer {
  key: string; slug: string; display_name: string;
  campus_live: number; interns: number; coops: number; new_grads: number; first_jobs: number;
  with_pay: number; with_deadline: number; next_deadline: string | null;
  hourly_median: number | null; annual_median: number | null; currency: string | null;
  cpa_firm: boolean; canada: number; remote: number; all_live: number; careers_host: string | null;
  terms: { term: string; n: number }[]; types: { type: string; n: number }[];
  roles: { role: string; slug: string; n: number }[]; cities: { slug: string; city: string; n: number }[];
  signals: Record<string, number>; signals_n: number;
  calendar: { month: string; n: number }[];
  layoffs: { announced_on: string; headcount: number | null; scope: string | null; source_url: string; source_name: string | null }[];
  refreshed_at: string;
}
export interface CampusRow {
  short_id: string; title: string; company: string; location: string | null; remote: boolean;
  type: string; term: string | null; apply_by: string | null; posted_at: string | null; url: string | null; country: string | null;
  pay_min: number | null; pay_max: number | null; period: string | null; currency: string | null; gpa_min: number | null;
  sponsorship: string | null; cpa_support: boolean; signals: Record<string, boolean | number> | null;
}
export interface CampusEmployerPage { meta: CampusEmployer; rows: CampusRow[]; ppr: { offices: number; provinces: string[] } | null; generated_at: string }
export function useCampusEmployers(limit = 50) {
  return useQuery({
    queryKey: ['campus_employers', limit],
    queryFn: () => unwrap<CampusEmployer[]>(rpc('campus_employers', { p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}
export function useCampusEmployer(slug: string | undefined, limit = 60) {
  return useQuery({
    queryKey: ['campus_employer_page', slug, limit],
    enabled: !!slug,
    queryFn: () => unwrap<CampusEmployerPage | null>(rpc('campus_employer_page', { p_slug: slug, p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

/** Which credential opens which door: postings that name a specific
 *  credential (high school .. bachelor's required), over the whole live index.
 *  Row shape is the open-door one; only the flag keys differ. */
/**
 * When the materialized-view chain these pages read was last rebuilt.
 *
 * Only for pages backed by an MV (open-door, entry-level). A page that counts
 * live from the index on every request must NOT show this: its numbers are
 * newer than the snapshot stamp, and claiming otherwise would understate them.
 */
export function useIndexFreshness() {
  return useQuery({
    queryKey: ['af_index_refreshed_at'],
    queryFn: async () => (await rpc('af_index_refreshed_at', {})).data as unknown as string | null,
    staleTime: 1000 * 60 * 10,
  });
}

export interface CredentialsPage {
  base: number | null;
  refreshed_at: string | null;
  /** every count on the page counts an employer at most `cap` postings; the
   *  *_raw numbers and view.capped carry the uncapped figures */
  cap: number;
  counts: Record<string, number>;
  ladder: { key: string; n: number; n_raw: number; remote: number; canada: number; median_usd: number | null; median_cad: number | null; top_role: string | null }[];
  view: { key: string; n: number; n_raw: number; remote: number; employers: number; with_pay: number; canada: number; capped: { company: string; n: number }[] };
  pay: { currency: string; n: number; p25: number; median: number; p75: number } | null;
  roles: { role: string; slug: string; n: number }[];
  employers_top: { company: string; n: number; remote: number }[];
  cities: { slug: string; city: string; n: number }[];
  rows: OpenDoorRow[];
}
export function useCredentials(view: string, remote: boolean | null, country: string | null, limit = 40) {
  return useQuery({
    queryKey: ['credentials_page', view, remote, country, limit],
    queryFn: () => unwrap<CredentialsPage>(rpc('credentials_page', { p_view: view, p_remote: remote, p_country: country, p_limit: limit })),
    staleTime: 1000 * 60 * 30,
    // Keep the current rows up (the page dims them) while a filter change loads.
    placeholderData: keepPreviousData,
  });
}

/** The counsellor one-pager: per-metro open-door counts and rows, entry-level
 *  roles, the programs that apply to the city's country and province, and the
 *  CPA training offices in the city. Joined to city_page() on the page. */
export interface CounsellorCity {
  meta: { slug: string; short_name: string; display_name: string; region: string | null; country_code: string | null };
  generated_at: string;
  doors: Record<string, number>;
  door_rows: OpenDoorRow[];
  door_employers: { company: string; n: number }[];
  entry: { n: number; zero_years: number; roles: { role: string; n: number }[] };
  entry_roles: string[];
  credentials: { key: string; n: number; remote: number }[];
  programs: { program: string; body: string; jurisdiction: string; audience: string; cycle: string; window_note: string | null; apply_url: string | null; source_url: string; finance_focus: string }[];
  ppr: { offices: number; firms: { firm: string; offices: number; live: number }[] } | null;
}
export function useCounsellorCity(slug: string | undefined) {
  return useQuery({
    queryKey: ['counsellor_city', slug],
    enabled: !!slug,
    queryFn: () => unwrap<CounsellorCity | null>(rpc('counsellor_city', { p_slug: slug })),
    staleTime: 1000 * 60 * 30,
  });
}

/** Open-door jobs: postings whose text opens a gate (no experience, no degree,
 *  will train, bookkeeping counts, veteran program, fair chance...). */
export interface OpenDoorRow {
  title: string; company: string; url: string | null; location: string | null; remote: boolean;
  posted_at: string | null; years: number | null; role: string | null; country: string | null;
  pay_min: number | null; pay_max: number | null; currency: string | null; period: string | null;
  flags: Record<string, boolean>;
}
export interface OpenDoorPage {
  counts: Record<string, number>;
  view: { key: string; n: number; remote: number; employers: number; with_pay: number; canada: number };
  pay: { currency: string; n: number; p25: number; median: number; p75: number } | null;
  roles: { role: string; slug: string; n: number }[];
  employers_top: { company: string; n: number; remote: number }[];
  cities: { slug: string; city: string; n: number }[];
  rows: OpenDoorRow[];
}
export function useOpenDoor(view: string, remote: boolean | null, country: string | null, limit = 40) {
  return useQuery({
    queryKey: ['open_door_page', view, remote, country, limit],
    queryFn: () => unwrap<OpenDoorPage>(rpc('open_door_page', { p_view: view, p_remote: remote, p_country: country, p_limit: limit })),
    staleTime: 1000 * 60 * 30,
    // Keep the current rows up (the page dims them) while a filter change loads.
    placeholderData: keepPreviousData,
  });
}

/** Government student and graduate hiring programs (Canada + US), every row
 *  read from the program's own page and dated. */
export interface GovProgram {
  id: number; country: 'CA' | 'US'; jurisdiction: string; body: string; program: string;
  audience: 'students' | 'recent_graduates' | 'both' | 'veterans' | 'career_changers' | 'newcomers' | 'unemployed' | 'all';
  finance_focus: 'finance-specific' | 'general';
  finance_note: string | null; cycle: string; window_note: string | null; pay_note: string | null;
  cpa_note: string | null; apply_url: string | null; source_url: string; verified_on: string; notes: string | null;
}
export interface GovProgramsPage {
  counts: { programs: number; jurisdictions: number; finance_specific: number; checked_on: string | null };
  rows: GovProgram[];
}
export function useGovPrograms() {
  return useQuery({
    queryKey: ['gov_student_programs_page'],
    queryFn: () => unwrap<GovProgramsPage>(rpc('gov_student_programs_page')),
    staleTime: 1000 * 60 * 60,
  });
}

export function useCpaPprPage(province: string | null) {
  return useQuery({
    queryKey: ['cpa_ppr_page', province],
    queryFn: () => unwrap<CpaPprPage>(rpc('cpa_ppr_page', { p_province: province })),
    staleTime: 1000 * 60 * 30,
  });
}

export interface EarlyCareerSignalStat { ec_type: string; n: number; signal: string; k: number; share: number; hourly_p25: number | null; hourly_median: number | null; hourly_p75: number | null }
/** Shares of intern, co-op and new-grad postings whose text states each signal. */
export function useEarlyCareerSignalStats() {
  return useQuery({
    queryKey: ['early_career_signal_stats'],
    queryFn: () => unwrap<EarlyCareerSignalStat[]>(rpc('early_career_signal_stats', {})),
    staleTime: 1000 * 60 * 30,
  });
}

export function useEarlyCareerMeta() {
  return useQuery({
    queryKey: ['early_career_meta'],
    queryFn: async () => (await unwrap<EarlyCareerMeta[]>(rpc('early_career_meta', {})))[0] ?? null,
    staleTime: 1000 * 60 * 10,
  });
}

// ---------- software-in-postings tracker ----------

export interface SoftwareToolRow {
  slug: string;
  label: string;
  category: 'erp' | 'close' | 'fpa' | 'analytics' | 'spend' | 'tax' | 'audit' | 'payroll' | 'ai';
  note: string | null;
  live_n: number;
  live_share: number | null;
  /** most recent COMPLETE month the trend math uses */
  m0: string | null;
  m0_share: number | null;
  /** share-point deltas vs 1 and 3 months ago — points, not percent-of */
  mom_delta: number | null;
  qoq_delta: number | null;
  series: number[] | null;
}

export function useSoftwareTracker() {
  return useQuery({
    queryKey: ['software_tracker'],
    queryFn: () => unwrap<SoftwareToolRow[]>(rpc('software_tracker', {})),
    staleTime: 1000 * 60 * 60,
  });
}

// ---------- return-to-office tracker ----------

export interface RtoTrendRow {
  industry: string;
  latest_month: string;
  latest_share: number;
  delta_3m: number | null;
  n_latest: number;
  series: number[];
}

export function useRtoTrend() {
  return useQuery({
    queryKey: ['rto_trend'],
    queryFn: () => unwrap<RtoTrendRow[]>(rpc('rto_trend', {})),
    staleTime: 1000 * 60 * 60,
  });
}

// ---------- hiring calendar + time to close ----------

export interface HiringCalendarRow {
  function_group: string;
  month: string;
  share: number;
  distinct_roles: number;
}
export function useHiringCalendar() {
  return useQuery({
    queryKey: ['hiring_calendar'],
    queryFn: () => unwrap<HiringCalendarRow[]>(rpc('hiring_calendar', {})),
    staleTime: 1000 * 60 * 60,
  });
}

export interface TimeToCloseRow {
  company: string;
  n_closed: number;
  median_days: number;
  p25_days: number | null;
  p75_days: number | null;
  overall_median: number;
  overall_n: number;
}
export function useTimeToClose() {
  return useQuery({
    queryKey: ['time_to_close'],
    queryFn: () => unwrap<TimeToCloseRow[]>(rpc('time_to_close', {})),
    staleTime: 1000 * 60 * 60,
  });
}

// ---------- Robert Half comparison ----------

export interface VsRobertHalfRow {
  canonical_role: string;
  rh_label: string;
  rh_low: number; rh_mid: number; rh_high: number;
  measured_p25: number; measured_p50: number; measured_p75: number;
  measured_n: number;
  /** how far RH's mid sits from our measured p50, in percent of p50 */
  mid_delta_pct: number;
  source_url: string;
  accessed_on: string;
  guide_label: string;
  /** aligned monthly series, ascending; p50 is null for months under the n gate */
  months: string[] | null;
  p50s: (number | null)[] | null;
  ns: number[] | null;
  refreshed_at: string | null;
}
export function useVsRobertHalf() {
  return useQuery({
    queryKey: ['vs_robert_half'],
    queryFn: () => unwrap<VsRobertHalfRow[]>(rpc('vs_robert_half', {})),
    staleTime: 1000 * 60 * 60,
  });
}

export interface BlsReferenceRow {
  occ_code: string;
  occ_label: string;
  p10: number; p25: number; p50: number; p75: number; p90: number;
  period_label: string;
  source_url: string;
  accessed_on: string;
  roles: { canonical_role: string; rh_label: string; measured_p50: number }[];
}
export function useBlsReference() {
  return useQuery({
    queryKey: ['bls_reference'],
    queryFn: () => unwrap<BlsReferenceRow[]>(rpc('bls_reference', {})),
    staleTime: 1000 * 60 * 60,
  });
}

export interface VsRhIndustryRow {
  canonical_role: string;
  industry: string;
  p50: number;
  n: number;
}
export function useVsRobertHalfIndustry() {
  return useQuery({
    queryKey: ['vs_robert_half_industry'],
    queryFn: () => unwrap<VsRhIndustryRow[]>(rpc('vs_robert_half_industry', {})),
    staleTime: 1000 * 60 * 60,
  });
}

export interface VsRhStateRow {
  canonical_role: string;
  state: string;
  p50: number;
  n: number;
}
export function useVsRobertHalfState() {
  return useQuery({
    queryKey: ['vs_robert_half_state'],
    queryFn: () => unwrap<VsRhStateRow[]>(rpc('vs_robert_half_state', {})),
    staleTime: 1000 * 60 * 60,
  });
}

/* ── City pages ────────────────────────────────────────────────────────────
 * One row per city that clears the gate, and one blob per city page.
 *
 * Pay comes back in the CITY'S OWN currency, not converted to USD. Vancouver's
 * Staff Accountant median is 67,500 CAD; the converted figure is 50,875, which
 * is wrong to every reader who lives there.
 */
export interface CityMetroRow {
  slug: string;
  display_name: string;
  short_name: string;
  region: string | null;
  country_code: string | null;
  jobs: number;
  employers: number;
  roles_priced: number;
  recruiters: number;
  currency: string;
  /** 'light' marks a smaller Canadian market listed below the full gate:
   *  every verified job shown, plus the remote-Canada band. */
  tier?: 'full' | 'light';
}

// Gate on substance, not on one section. roles_priced was a proxy for "is this
// city worth a page" and it mis-fires: Metro Montreal has 373 jobs from 126
// employers and no priced role, so it stayed invisible for want of a salary
// table it does not need to be useful. CityPage already guards every section
// with .length, so a city without medians simply renders without that block.
// The jobs and employers gates are left to city_metros()'s own defaults, so
// the database decides which cities get a card. Those defaults moved with
// migration 20261003060100 (each posting counted once), and leaving them out
// here means that migration is the only switch: publishing this build changes
// nothing until the migration is applied. Pass a value only to override.
export function useCityMetros(minJobs?: number, minRoles = 0, minEmployers?: number) {
  return useQuery({
    queryKey: ['city_metros', minJobs ?? null, minRoles, minEmployers ?? null],
    queryFn: () => unwrap<CityMetroRow[]>(rpc('city_metros', {
      p_min_roles: minRoles,
      ...(minJobs != null ? { p_min_jobs: minJobs } : {}),
      ...(minEmployers != null ? { p_min_employers: minEmployers } : {}),
    })),
    staleTime: 1000 * 60 * 60,
  });
}

export interface CityJob {
  title: string; company: string; url: string | null;
  posted_at: string | null; source: 'ats' | 'feed'; role?: string | null;
  comp_min?: number | null; comp_max?: number | null; currency?: string | null;
}
export interface CitySalaryRow {
  role: string; role_slug: string; group: string;
  n: number; p25: number; median: number; p75: number; currency: string;
}
export interface CityPageData {
  meta: {
    slug: string; display_name: string; short_name: string;
    country_code: string | null; region: string | null; sample_size: number;
  } | null;
  counts: { jobs: number; employers: number; direct_from_ats: number; early_career: number; recruiters: number };
  salary: CitySalaryRow[];
  jobs: CityJob[];
  feed_jobs: CityJob[];
  employers: { company: string; n: number; direct: boolean; url: string | null }[];
  recruiters: { name: string; title: string | null; company: string | null; status: string; reason: string }[];
  early_career: { title: string; company: string; url: string | null; posted_at: string | null }[];
  /** Present only on light-tier (small Canadian market) payloads. */
  tier?: 'light';
  remote_ca?: { total: number; employers: number; with_pay: number; jobs: CityJob[] };
  nearest?: { slug: string; name: string; jobs: number }[];
  /** ESDC Job Bank wage benchmarks (hourly CAD) for the city's economic
   *  region: the pay table a small local sample cannot support. */
  gov_pay?: {
    region: string; edition: string;
    rows: { occupation: string; noc: string; low: number | null; median: number; high: number | null; period: string }[];
  };
  /** Provincial public-sector pay disclosures mapped to this city (Canadian
   *  cities with at least five disclosed finance roles). */
  public_pay?: {
    province: string; city: string; page: string; year: number; rows: number; employers: number;
    median: number; basis: 'base_salary' | 'compensation' | 'mixed';
    top: { title: string; employer: string; salary: number }[];
  };
}

export function useCityPage(slug: string | undefined) {
  return useQuery({
    queryKey: ['city_page', slug],
    enabled: !!slug,
    queryFn: () => unwrap<CityPageData>(rpc('city_page', { p_slug: slug, p_job_limit: 24 })),
    staleTime: 1000 * 60 * 15,
  });
}

/* ── Entry level ───────────────────────────────────────────────────────────
 * Seniority read from what the employer STATED, never from the title. Counting
 * by title gave 3,529 "new grad" roles, 2,905 of them from the words "Staff
 * Accountant" and 324 self-contradictory ("Senior Staff Accountant").
 */
export interface EntryLevelData {
  counts: {
    roles: number; employers: number; zero_years: number; one_year: number;
    two_years: number; with_pay: number; veteran: number; career_change: number;
  };
  pay: { currency: string; n: number; p25: number; median: number; p75: number } | null;
  by_role: { role: string; n: number }[];
  employers: { company: string; n: number }[];
  cities: { slug: string; city: string; n: number }[];
  no_experience: { title: string; company: string; url: string | null; posted_at: string | null; years: number }[];
  roles: { title: string; company: string; url: string | null; posted_at: string | null; years: number; role: string | null; remote: boolean }[];
}

export function useEntryLevel(limit = 24) {
  return useQuery({
    queryKey: ['entry_level_page', limit],
    queryFn: () => unwrap<EntryLevelData>(rpc('entry_level_page', { p_limit: limit })),
    staleTime: 1000 * 60 * 30,
  });
}

/** The two city sections that use data nobody else holds: postings beating the
 *  median for their own role IN THAT CITY, and employers who already hire in the
 *  city and also have remote roles open. Separate call from useCityPage so a
 *  slow extra never delays the page's main body. */
export interface CityAboveMarket {
  title: string; company: string; url: string | null;
  pay: number; median: number; multiple: number; currency: string;
  posted_at: string | null;
}
export interface CityRemoteEmployer { company: string; n: number; url: string | null }
export interface CityExtras {
  currency: string;
  above_market: CityAboveMarket[];
  above_market_total: number;
  remote: CityRemoteEmployer[];
  remote_total: number;
  remote_employers: number;
}

export function useCityExtras(slug: string | undefined) {
  return useQuery({
    queryKey: ['city_extras', slug],
    enabled: !!slug,
    queryFn: () => unwrap<CityExtras>(rpc('city_extras_cached', { p_slug: slug })),
    staleTime: 1000 * 60 * 15,
  });
}

/* ── FP&A and corporate finance ladder ─────────────────────────────────────
 * Pay is US-only on purpose. salary_min_usd is CONVERTED for non-US rows, so a
 * mixed ladder would compare a converted EUR band against a real USD one. 91%
 * of the pay data is US, so the loss is small and the comparison stays honest.
 *
 * Reads the materialized copy: the live builder takes 3.1s, which is past the
 * 3s ANON statement timeout, so every logged-out visitor got a 500. It measured
 * fine as service_role (8s), which is exactly how that trap hides.
 */
export interface FpaRung {
  role: string; slug: string; level: string;
  n: number; p25: number; median: number; p75: number;
}
export interface FpaPremiumRow {
  role: string; slug: string; n: number;
  p25: number; median: number; p75: number;
  yrs: number | null; n_yrs: number;
}
export interface FpaData {
  counts: { postings: number; employers: number; with_pay: number; roles: number; metros: number };
  ladder: FpaRung[];
  premium: FpaPremiumRow[];
  cfo_bands: { sector: string; n: number; median: number }[];
  metros: { slug: string; name: string; n: number; median: number | null }[];
  employers: { company: string; n: number; url: string | null }[];
  jobs: {
    title: string; company: string; url: string | null; posted_at: string | null;
    role: string | null; pay_min: number | null; pay_max: number | null;
  }[];
  built_at?: string;
}

export function useFpaPage() {
  return useQuery({
    queryKey: ['fpa_page'],
    queryFn: () => unwrap<FpaData>(rpc('fpa_page_cached', {})),
    staleTime: 1000 * 60 * 30,
  });
}

/* ── remote flagships (/data/remote-us, /data/remote-ca) ─────────────────── */

export interface RemotePremiumRow {
  role: string; slug: string;
  remote_n: number; office_n: number;
  remote_median: number; office_median: number;
  diff: number; diff_pct: number;
}

export interface RemoteRoleRow {
  role: string; slug: string; n: number; priced_n: number;
  p25: number | null; median: number | null; p75: number | null;
}

export interface RemotePageData {
  country: 'US' | 'CA';
  currency: 'USD' | 'CAD';
  counts: {
    postings: number; employers: number; with_pay: number;
    disclosure_pct: number | null; new_7d: number; internships: number;
  };
  pay: { n: number; p25: number | null; p50: number | null; p75: number | null; p90: number | null };
  roles: RemoteRoleRow[];
  premium: RemotePremiumRow[];
  employers: { company: string; n: number; url: string | null }[];
  remote_first: { company: string; industry: string | null; remote_pct: number | null; roles: number | null; url: string | null }[];
  internship_examples: { title: string; company: string; url: string | null; posted_at: string | null }[];
  jobs: {
    title: string; company: string; url: string | null; posted_at: string | null;
    role: string | null; pay_min: number | null; pay_max: number | null;
    pay_currency: 'USD' | 'CAD' | null;
  }[];
  built_at?: string;
}

/* ── skills and courses (/data/skills) ──────────────────────────────────── */

export interface SkillPremiumRow {
  skill: string; premium: number; med_with: number; med_without: number;
  roles: number; priced: number; mentions: number; share: number;
}

export interface SkillsPageData {
  counts: { postings: number; priced: number; skills_tracked: number; skills_published: number };
  premium: SkillPremiumRow[];
  demand: { skill: string; n: number; share: number }[];
  courses: { skill: string; provider: string; url: string; affiliate: boolean }[];
  any_affiliate: boolean;
  built_at?: string;
}

export function useSkillsPage() {
  return useQuery({
    queryKey: ['skills_page'],
    queryFn: () => unwrap<SkillsPageData>(rpc('skills_page_cached', {})),
    staleTime: 1000 * 60 * 30,
  });
}

export function useRemotePage(country: 'US' | 'CA') {
  return useQuery({
    queryKey: ['remote_page', country],
    queryFn: () => unwrap<RemotePageData>(rpc('remote_page_cached', { p_country: country })),
    staleTime: 1000 * 60 * 30,
  });
}
